// 클레임 기준 변경이력 — 화면 오른쪽 패널. load(params) = claimsAPI.standardsHistory | reasonsHistory.
// 서버 응답 {items:[{id, entity, entity_key, action, before, after, reason, changed_at, changed_by_name}], next_before_id}.
// 시각은 UTC naive → KST(fmtKstDateTime) · 바뀐 칸마다 «전 → 후» · «더 보기» 는 before_id 로 다음 쪽.
import { useEffect, useState } from 'react';
import { Button } from '../../components/ui';
import { fmtKstDateTime } from '../../utils/kstTime';
import { COLORS } from '../../utils/uiStyles';
import { ACTION_LABELS, ENTITY_LABELS, FIELD_LABELS, errorText, historyValue } from './claimLabels';

const PAGE = 50;

// before·after 의 칸을 합쳐 «칸: 전 → 후» 줄로(생성·연결은 전이 없고, 연결 해제는 후가 없다).
function diffLines(before, after) {
  const b = before && typeof before === 'object' ? before : {};
  const a = after && typeof after === 'object' ? after : {};
  const keys = [];
  [...Object.keys(b), ...Object.keys(a)].forEach((k) => { if (!keys.includes(k)) keys.push(k); });
  return keys.map((k) => ({
    key: k,
    label: FIELD_LABELS[k] || k,
    before: Object.prototype.hasOwnProperty.call(b, k) ? historyValue(b[k]) : '—',
    after: Object.prototype.hasOwnProperty.call(a, k) ? historyValue(a[k]) : '—',
  }));
}

// subtitle(선택) — 제목 아래 한 줄(코드별 이력이면 '{코드} {사유}'). aria-label 은 '변경이력' 그대로(e2e 가 그 이름으로 찾는다).
export default function ClaimHistoryPanel({ load, onClose, subtitle }) {
  const [items, setItems] = useState([]);
  const [next, setNext] = useState(null);
  const [state, setState] = useState('loading');   // loading | ready | error
  const [error, setError] = useState('');
  const [more, setMore] = useState(false);

  useEffect(() => {
    let alive = true;
    Promise.resolve(load({ limit: PAGE })).then((r) => {
      if (!alive) return;
      const data = (r && r.data) || {};
      setItems(Array.isArray(data.items) ? data.items : []);
      setNext(data.next_before_id || null);
      setState('ready');
    }).catch((e) => {
      if (!alive) return;
      setError(errorText(e, '변경이력을 불러오지 못했습니다'));
      setState('error');
    });
    return () => { alive = false; };
  }, [load]);

  const loadMore = async () => {
    if (!next || more) return;
    setMore(true);
    try {
      const r = await load({ limit: PAGE, before_id: next });
      const data = (r && r.data) || {};
      setItems((prev) => [...prev, ...(Array.isArray(data.items) ? data.items : [])]);
      setNext(data.next_before_id || null);
    } catch (e) {
      setError(errorText(e, '변경이력을 더 불러오지 못했습니다'));
    } finally {
      setMore(false);
    }
  };

  return (
    <aside aria-label="변경이력" data-testid="claim-history-panel"
      style={{ width: '340px', flexShrink: 0, borderLeft: `1px solid ${COLORS.borderSoft}`, padding: '16px', overflowY: 'auto', background: COLORS.surface, boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
        <h2 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: COLORS.text }}>변경이력</h2>
        <Button variant="quiet" size="sm" style={{ marginLeft: 'auto' }} onClick={() => onClose && onClose()}>닫기</Button>
      </div>
      {subtitle && (
        <div data-testid="claim-history-subtitle" style={{ margin: '-6px 0 10px', fontSize: '12px', color: COLORS.textSub }}>{subtitle}</div>
      )}
      {state === 'loading' && <div role="status" style={{ fontSize: '12px', color: COLORS.textMuted }}>불러오는 중…</div>}
      {error && <div role="alert" style={{ fontSize: '12px', color: COLORS.dangerText, marginBottom: '8px' }}>{error}</div>}
      {state === 'ready' && items.length === 0 && (
        <div style={{ fontSize: '12px', color: COLORS.textMuted }}>아직 바뀐 내용이 없습니다.</div>
      )}
      <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {items.map((it) => (
          <li key={it.id} data-testid="claim-history-item" style={{ padding: '10px 0', borderBottom: `1px solid ${COLORS.borderSoft}`, fontSize: '12px', color: COLORS.textSub }}>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'baseline' }}>
              <span style={{ color: COLORS.textMuted }}>{fmtKstDateTime(it.changed_at)}</span>
              <span style={{ color: COLORS.text, fontWeight: 500 }}>{it.changed_by_name || '—'}</span>
            </div>
            <div style={{ marginTop: '3px', color: COLORS.text }}>
              {ENTITY_LABELS[it.entity] || it.entity} <span style={{ color: COLORS.textMuted }}>{it.entity_key}</span>
              {' · '}{ACTION_LABELS[it.action] || it.action}
            </div>
            {diffLines(it.before, it.after).map((d) => (
              <div key={d.key} style={{ marginTop: '2px', wordBreak: 'break-all' }}>
                {d.label}: {d.before} → {d.after}
              </div>
            ))}
            {it.reason && <div style={{ marginTop: '3px' }}>사유: {it.reason}</div>}
          </li>
        ))}
      </ol>
      {next && (
        <div style={{ marginTop: '10px' }}>
          <Button variant="secondary" size="sm" onClick={loadMore} disabled={more}>{more ? '불러오는 중…' : '더 보기'}</Button>
        </div>
      )}
    </aside>
  );
}
